import React from "react";
import { useSelector } from "react-redux";
import styles from "./TreeLayout.module.scss";
import classNames from "classnames";
import { Tooltip } from "react-tooltip";

const TreeLayout = () => {
  const { findingsData, auditsTasksData, risksData } = useSelector(
    (state) => state.organization,
  );
  const { meModeActive } = useSelector((state) => state.filters);
  const currentUser = useSelector((state) => state.user.email);

  const impactScores = { low: 1, medium: 2, high: 3 };
  const getImpact = (level) => impactScores[level?.toLowerCase?.()] ?? 0;
  const isValidLevel = (level) =>
    typeof level === "string" &&
    ["low", "medium", "high"].includes(level.toLowerCase());

  const isValidFinding = (findingId, findingsData) => {
    return findingsData.hasOwnProperty(findingId);
  };

  const mainChapters = {};
  let directFindingsCount = 0;
  let directFindings = [];

  Object.values(risksData).forEach((risk) => {
    const mainChapter = risk.qms_id[0];

    if (!mainChapters[mainChapter]) {
      mainChapters[mainChapter] = {
        auditsCount: 0,
        linkedFindingsCount: 0,
        totalFindingsCount: 0,
        rpsCount: 0,
        improvementsCount: 0,
        finalRpsCount: 0,
        linkedFindings: [],
        audits: [],
        linkedImprovements: [],
      };
    }
  });

  // NEW HARDCODED DEFINE TEXT IN BOXES
  const mainChapterLabels = {
    A: "A | Company Information",
    B: "B | Systems & Data",
    C: "C | Global Supply Chain",
    D: "D | Accounting & Finance",
    E: "E | Operations & Customs",
    F: "F | Suppliers",
    G: "G | People & Talent",
    H: "H | Safety & Security",
  };

  // OLD LOGIC FOR RENDERING DEFINE TEXT IN BOXES
  // Object.values(risksData).forEach((risk) => {
  //   const qmsId = risk.qms_id;
  //   const description = risk.qms_description;
  //
  //   if (
  //     typeof qmsId === "string" &&
  //     qmsId.length > 0 &&
  //     typeof description === "string" &&
  //     description.trim().length > 0
  //   ) {
  //     const mainChapter = qmsId[0].toUpperCase();
  //
  //     if (!mainChapter.match(/^[A-Z]$/)) return;
  //
  //     if (!mainChapterLabels[mainChapter]) {
  //       mainChapterLabels[mainChapter] = `${mainChapter}: ${description}`;
  //     }
  //   }
  // });

  Object.values(auditsTasksData)
    .filter((auditTask) => !auditTask.archived)
    .forEach((auditTask) => {
      const countedChapters = new Set();
      const auditKey = auditTask.key;

      auditTask.form.forEach((formEntry) => {
        if (formEntry.risk && formEntry.risk.qms_id) {
          const auditChapter = formEntry.risk.qms_id[0];

          const level = formEntry.risk?.level;
          const isValid = isValidLevel(level);

          const isRelevantAudit =
            !meModeActive ||
            auditTask.linked_findings.some(
              (findingId) =>
                isValidFinding(findingId, findingsData) &&
                [
                  findingsData[findingId].responsible,
                  findingsData[findingId].accountable,
                  findingsData[findingId].consulted,
                  findingsData[findingId].informed,
                ].some((role) =>
                  Array.isArray(role)
                    ? role.includes(currentUser)
                    : role === currentUser,
                ),
            );

          if (
            mainChapters[auditChapter] &&
            !countedChapters.has(auditChapter) &&
            isRelevantAudit
          ) {
            mainChapters[auditChapter].auditsCount += 1;
            mainChapters[auditChapter].audits.push(auditKey);
            countedChapters.add(auditChapter);

            const validLinkedFindings = auditTask.linked_findings.filter(
              (findingId) =>
                isValidFinding(findingId, findingsData) &&
                !findingsData[findingId].archived &&
                (!meModeActive ||
                  [
                    findingsData[findingId].responsible,
                    findingsData[findingId].accountable,
                    findingsData[findingId].consulted,
                    findingsData[findingId].informed,
                    findingsData[findingId].reporter,
                  ].some((role) =>
                    Array.isArray(role)
                      ? role.includes(currentUser)
                      : role === currentUser,
                  )),
            );

            mainChapters[auditChapter].linkedFindingsCount +=
              validLinkedFindings.length;
            mainChapters[auditChapter].linkedFindings.push(
              ...validLinkedFindings,
            );
          }
        }
      });
    });

  Object.values(findingsData)
    .filter((finding) => !finding.archived)
    .forEach((finding) => {
      const isRelevantFinding =
        !meModeActive ||
        [
          finding.responsible,
          finding.accountable,
          finding.consulted,
          finding.informed,
        ].some((role) =>
          Array.isArray(role)
            ? role.includes(currentUser)
            : role === currentUser,
        );

      if (isRelevantFinding) {
        const chapter = finding.risk?.qms_id?.[0];
        const level = finding.risk?.level;

        if (!chapter || !mainChapters[chapter]) return;

        mainChapters[chapter].totalFindingsCount += 1;

        if (isValidLevel(level)) {
          const findingRps = getImpact(level);
          mainChapters[chapter].rpsCount += findingRps;

          if (finding.linked_improvements?.length > 0) {
            mainChapters[chapter].improvementsCount +=
              finding.linked_improvements.length;
            mainChapters[chapter].linkedImprovements.push(
              ...finding.linked_improvements,
            );

            mainChapters[chapter].finalRpsCount += 0;
          } else {
            mainChapters[chapter].finalRpsCount += findingRps;
          }
        } else {
          // No impact on RPS, but include in counts
          if (finding.linked_improvements?.length > 0) {
            mainChapters[chapter].improvementsCount +=
              finding.linked_improvements.length;
            mainChapters[chapter].linkedImprovements.push(
              ...finding.linked_improvements,
            );
          }
        }

        if (finding.linked_audittasks.length === 0) {
          directFindingsCount += 1;
          directFindings.push(finding.key);
        }
      }
    });

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getAllRelevantDates = () => {
    const relevantAuditDates = Object.values(auditsTasksData)
      .filter((auditTask) => !auditTask.archived)
      .filter((auditTask) => {
        if (!meModeActive) return true;

        return auditTask.linked_findings.some((findingId) => {
          const finding = findingsData[findingId];
          if (!finding || finding.archived) return false;

          return [
            finding.responsible,
            finding.accountable,
            finding.consulted,
            finding.informed,
          ].some((role) =>
            Array.isArray(role)
              ? role.includes(currentUser)
              : role === currentUser,
          );
        });
      })
      .map((task) => task.date)
      .filter(Boolean);

    const relevantFindingDates = Object.values(findingsData)
      .filter((finding) => !finding.archived)
      .filter((finding) => {
        if (!meModeActive) return true;

        return [
          finding.responsible,
          finding.accountable,
          finding.consulted,
          finding.informed,
          finding.reporter,
        ].some((role) =>
          Array.isArray(role)
            ? role.includes(currentUser)
            : role === currentUser,
        );
      })
      .map((finding) => finding.date)
      .filter(Boolean);

    const all = [...relevantAuditDates, ...relevantFindingDates]
      .map((d) => new Date(d))
      .filter((d) => !isNaN(d.getTime()))
      .sort((a, b) => a - b);

    return {
      start: all[0],
      end: all[all.length - 1],
    };
  };

  const { start: firstDate, end: lastDate } = getAllRelevantDates();

  return (
    <div className={styles.treeLayout}>
      <div className={styles.columnTitles}>
        <div className={styles.columnTitle}>Define</div>
        <div className={styles.columnTitle}>Measure</div>
        <div className={styles.columnTitle}>Analyse</div>
        <div className={styles.columnTitle}>Improve</div>
        <div className={styles.columnTitle}>Control</div>
      </div>
      {Object.entries(mainChapters)
        .sort(([chapterA], [chapterB]) => chapterA.localeCompare(chapterB)) // Sorting alphabetically by chapter name
        .map(([chapter, data], index) => {
          return (
            <div className={styles.chapterRow} key={index}>
              <LineComponent />
              <ChapterBox
                chapterLabel={
                  mainChapterLabels[chapter] || `Chapter ${chapter}`
                }
              />
              <div className={styles.measure}>
                <AuditBox
                  auditsCount={data.auditsCount}
                  audits={data.audits}
                  tooltipId={`audit-tooltip-${chapter}`}
                />
                <FindingBox
                  findingsCount={data.linkedFindingsCount}
                  linkedFindings={data.linkedFindings}
                  tooltipId={`finding-tooltip-${chapter}`}
                />
              </div>
              <RPSBox rpsCount={data.rpsCount} />
              <ImprovementBox
                improvementsCount={data.improvementsCount}
                linkedImprovements={data.linkedImprovements}
                tooltipId={`improvement-tooltip-${chapter}`}
              />
              <FinalRPSBox finalRpsCount={data.finalRpsCount} />
            </div>
          );
        })}
      <div className={styles.directFindingsRow}>
        <HalfLineComponent />
        <div className={styles.placeholderBox} /> {/* Empty Define column */}
        <div className={classNames(styles.measure, styles.lastRow)}>
          <div className={styles.directFindingsBox}>Direct</div>
          <FindingBox
            findingsCount={directFindingsCount}
            linkedFindings={directFindings}
            tooltipId="direct-findings-tooltip"
          />
        </div>
        <div className={styles.placeholderBox} /> {/* Empty Control column */}
      </div>
      <Tooltip />
      {firstDate && lastDate && (
        <div className={styles.dateRange}>
          Showing data from <strong>{formatDate(firstDate)}</strong> to{" "}
          <strong>{formatDate(lastDate)}</strong>
          {meModeActive && <span> (Me Mode)</span>}
        </div>
      )}
      {!firstDate && !lastDate && meModeActive && (
        <div className={styles.dateRange}>
          No relevant data found for Me Mode
        </div>
      )}
    </div>
  );
};

const ChapterBox = ({ chapterLabel }) => (
  <div className={styles.chapterBox}>
    <div>{chapterLabel}</div>
  </div>
);

const AuditBox = ({ auditsCount, audits, tooltipId }) => (
  <div
    className={classNames(
      styles.auditBox,
      auditsCount === 0 && styles.emptyBox,
    )}
    data-tooltip-id={tooltipId}
    data-tooltip-content={`${audits.join(", ")}`}
  >
    <div>{auditsCount} Audits</div>
    <Tooltip id={tooltipId} place="top" className={styles.tooltip} />
  </div>
);

const FindingBox = ({ findingsCount, linkedFindings, tooltipId }) => (
  <div
    className={classNames(
      styles.findingBox,
      findingsCount === 0 && styles.emptyBox,
    )}
    data-tooltip-id={tooltipId}
    data-tooltip-content={`${linkedFindings?.join(", ")}`}
  >
    <div>{findingsCount} Findings</div>
    <Tooltip id={tooltipId} place="top" className={styles.tooltip} />
  </div>
);

const RPSBox = ({ rpsCount }) => (
  <div className={styles.rpsBox}>
    <div>{rpsCount} Exposure</div>
  </div>
);

const ImprovementBox = ({
  improvementsCount,
  linkedImprovements,
  tooltipId,
}) => (
  <div
    className={classNames(
      styles.improvementBox,
      improvementsCount === 0 && styles.emptyBox,
    )}
    data-tooltip-id={tooltipId}
    data-tooltip-content={`${linkedImprovements.join(", ")}`}
  >
    <div>{improvementsCount} Improvements</div>
    <Tooltip id={tooltipId} place="top" className={styles.tooltip} />
  </div>
);

const FinalRPSBox = ({ finalRpsCount }) => (
  <div className={styles.finalRpsBox}>
    <div>{finalRpsCount || "0"} RPS</div>
  </div>
);

const LineComponent = () => <div className={styles.line}></div>;

const HalfLineComponent = () => <div className={styles.halfLine}></div>;

export default TreeLayout;
